
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  HomePageGetter,
  HomePageMutation,
  HomePageWidgetType,
  IndexData,
  SlideIndexMapParams
} from "@/components/homePage/types";
import Swiper, { SwiperOptions } from "swiper";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import { Action, Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import { RouteName } from "@/router/types";
import {
  ChannelChangeAction,
  ChannelChangeMutation
} from "@/components/channelChange/types";
import { ChannelEpgParams, OverlayAction } from "@/components/overlay/types";
import dayjs from "dayjs";
import {
  BuffetEpgItem,
  Movie,
  MyChannel,
  TvShow
} from "@/services/apiService/api.types";
import apiStorageInterface from "@/services/apiStorageInterface";
import SpatialNavigation from "spatial-navigation-js";
import { isMovie, isTvShow, scrollToPage } from "@/helpers/utils";
import { VodPageMutation } from "@/components/VoD/types";

@Component({})
export default class HomePageWidgetSlider extends Vue {
  @Prop() readonly items!: HomePageWidgetType;
  @Prop() readonly name!: string;
  @Prop() readonly myIndex!: number;
  @Prop() readonly smallSwiper!: boolean;

  @Action(ChannelChangeAction.UpdateSource, {
    namespace: Namespace.ChannelChange
  })
  updateSource!: (src: string) => void;

  @Action(ChannelChangeAction.UpdateOffset, {
    namespace: Namespace.ChannelChange
  })
  updateOffset!: (offset: number) => void;

  @Action(OverlayAction.UpdateChannelEpg, {
    namespace: Namespace.Overlay
  })
  updateChannelEpg!: (channelEpgParams?: ChannelEpgParams) => Promise<void>;

  @Mutation(ChannelChangeMutation.SetChannelId, {
    namespace: Namespace.ChannelChange
  })
  setChannelId!: (channelId: number) => void;

  @Mutation(HomePageMutation.setPlayedWidgetIndex, {
    namespace: Namespace.HomePage
  })
  setPlayedWidgetIndex!: (widgetItemIndex?: IndexData) => void;

  @Mutation(HomePageMutation.setActiveSlideIndexMap, {
    namespace: Namespace.HomePage
  })
  setActiveSlideIndexMap!: (params?: SlideIndexMapParams) => void;

  @Mutation(HomePageMutation.setWidgetKeyControl, {
    namespace: Namespace.HomePage
  })
  setWidgetKeyControl!: (status: boolean) => void;

  @Mutation(VodPageMutation.SetPromoVod, {
    namespace: Namespace.Vod
  })
  setPromoVod!: (promoVoD: Movie | TvShow | null) => void;

  @Getter(HomePageGetter.GetActiveSlideIndexMap, {
    namespace: Namespace.HomePage
  })
  getActiveSlideIndexMap!: Array<number>;

  @Getter(HomePageGetter.GetPlayedWidgetIndex, {
    namespace: Namespace.HomePage
  })
  getPlayedWidgetIndex?: IndexData;

  @Getter(HomePageGetter.GetWidgetKeyControl, {
    namespace: Namespace.HomePage
  })
  getWidgetKeyControl!: boolean;

  swiperOptions: SwiperOptions = {
    keyboard: {
      enabled: false
    },
    slidesPerView: 5,
    spaceBetween: 40,
    breakpoints: {
      // when window width is <= 768px
      768: {
        slidesPerView: 3,
        spaceBetween: 10
      },
      1200: {
        slidesPerView: 5,
        spaceBetween: 20
      }
    }
  };

  swiperLocal: Swiper | undefined;

  scrollFuncRef = scrollToPage;

  mounted(): void {
    this.$nextTick(() => {
      this.swiperLocal = new Swiper(
        "#swiper" + this.myIndex,
        this.swiperOptions
      );

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      this.swiperLocal.on("slideChangeTransitionEnd", () => {
        if (this.getPlayedWidgetIndex) {
          const { valueIndex, value } = this.getPlayedWidgetIndex;
          SpatialNavigation.pause();
          SpatialNavigation.focus("#widget" + valueIndex + "slide" + value);
          this.setWidgetKeyControl(true);

          this.setPlayedWidgetIndex();
        }
      });
      if (
        this.getPlayedWidgetIndex?.valueIndex !== undefined &&
        this.getPlayedWidgetIndex?.valueIndex === this.myIndex
      ) {
        this.focusWidgetItem();
      }
    });
  }

  handleSwiperActions(event: KeyboardEvent): void {
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_LEFT:
        if (
          (this.getActiveSlideIndexMap[this.myIndex] >= 2 &&
            this.getActiveSlideIndexMap[this.myIndex] <=
              this.items.length - 3) ||
          (this.smallSwiper &&
            this.getActiveSlideIndexMap[this.myIndex] >= 1 &&
            this.getActiveSlideIndexMap[this.myIndex] <= this.items.length - 2)
        ) {
          this.swiperLocal?.slidePrev();
        }
        break;
      case KeySet.KEY_RIGHT:
        if (
          (this.getActiveSlideIndexMap[this.myIndex] >= 2 &&
            this.getActiveSlideIndexMap[this.myIndex] <=
              this.items.length - 3) ||
          (this.smallSwiper &&
            this.getActiveSlideIndexMap[this.myIndex] >= 1 &&
            this.getActiveSlideIndexMap[this.myIndex] <= this.items.length - 2)
        ) {
          this.swiperLocal?.slideNext();
        }
        break;
      case KeySet.KEY_ENTER:
        this.widgetPlayback(this.getActiveSlideIndexMap[this.myIndex]);
        break;
    }
  }

  get imgPlaceholder(): string {
    return require("/src/assets/images/square-image-placeholder.jpg");
  }

  widgetPlayback(slideIndex: number): void {
    // setting index of clicked/played item for refocus if returning to home page
    this.setPlayedWidgetIndex({
      valueIndex: this.myIndex,
      value: slideIndex
    });
    this.playContent(slideIndex);
  }

  async playContent(slideIndex: number): Promise<void> {
    if (isMovie(this.items[slideIndex]) || isTvShow(this.items[slideIndex])) {
      this.setPromoVod(this.items[slideIndex] as Movie | TvShow);
      await this.$router.push({ name: RouteName.VoD });
    }
    // eslint-disable-next-line no-prototype-builtins
    if (this.items[slideIndex].hasOwnProperty("videoLive")) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      this.updateSource(this.items[slideIndex].videoLive);
      this.setChannelId(this.items[slideIndex].id);
      this.updateOffset(0);
      await this.updateChannelEpg();
    }

    // eslint-disable-next-line no-prototype-builtins
    if (!this.items[slideIndex].hasOwnProperty("videoLive")) {
      let offset = dayjs().diff(
        (this.items[slideIndex] as BuffetEpgItem).starttime,
        "second"
      );
      //channel data for this EPG Item
      const channels = await apiStorageInterface.fetchCustomChannelList();
      let channel = channels.find(
        (channel) =>
          channel.id === (this.items[slideIndex] as BuffetEpgItem).channel_id
      ) as MyChannel;

      this.updateOffset(offset);
      this.updateSource(channel.videoLive);
      this.setChannelId(channel.id);
      await this.updateChannelEpg({
        date: dayjs((this.items[slideIndex] as BuffetEpgItem).starttime).format(
          "YYYY-MM-DD"
        )
      });
    }

    await this.$router.push({ name: RouteName.Player });
  }

  focusWidgetItem(): void {
    //check whether 'getPlayedWidgetIndex' is set in the 'mounted' method
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { valueIndex, value } = this.getPlayedWidgetIndex!;

    if (this.smallSwiper) {
      //in case the channel does not require sliding (does not trigger event)
      if (value < 2) {
        SpatialNavigation.pause();
        this.scrollFuncRef("widget" + valueIndex + "slide" + value);
        SpatialNavigation.focus("#widget" + valueIndex + "slide" + value);
        this.setWidgetKeyControl(true);

        this.setPlayedWidgetIndex();
        return;
      }

      if (value > 1 && value <= this.items.length - 2) {
        this.swiperLocal?.slideTo(value - 1);
        return;
      }

      if (value >= this.items.length - 2) {
        this.swiperLocal?.slideTo(this.items.length - 3);
        return;
      }
    }

    //in case the widget does not require sliding (does not trigger event)
    if (value < 3) {
      SpatialNavigation.pause();
      SpatialNavigation.focus("#widget" + valueIndex + "slide" + value);
      this.scrollFuncRef("widget" + valueIndex + "slide" + value);
      this.setWidgetKeyControl(true);

      this.setPlayedWidgetIndex();
      return;
    }

    if (value >= this.items.length - 3) {
      this.swiperLocal?.slideTo(this.items.length - 5);
      return;
    }

    if (value > 2 && value < this.items.length - 3) {
      this.swiperLocal?.slideTo(value - 2);
    }
  }

  beforeDestroy(): void {
    this.swiperLocal?.off("click");
  }
}
